import { Injectable, NgZone, inject, isDevMode, signal } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, HttpTransportType, LogLevel } from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { OidcAuthService } from '@app/core/services/oidc-auth.service';
import { EnvironmentService } from '@app/core/services/environment.service';
import { EVENT_NAMES, NotificationEventMap, NotificationEventName } from './notification-events';

export type NotificationConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

export interface NotificationEnvelope<K extends NotificationEventName = NotificationEventName> {
  event: K;
  payload: NotificationEventMap[K];
  receivedAt: Date;
}

/**
 * Client for the API's realtime notification hub (/hubs/notifications).
 * Handlers are registered before start(), so subscribers can subscribe at any time.
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private oidcAuthService = inject(OidcAuthService);
  private envService = inject(EnvironmentService);
  private zone = inject(NgZone);

  private connection: HubConnection | null = null;
  private starting: Promise<void> | null = null;
  private readonly events = new Subject<NotificationEnvelope>();

  readonly state = signal<NotificationConnectionState>('disconnected');
  readonly events$ = this.events.asObservable();

  constructor() {
    if (isDevMode()) {
      // Lets developers simulate hub events from the console, e.g.
      // __notifications.emit('reporting.reportrun.completed', { reportRunId: '1', reportId: '2' })
      (window as any).__notifications = { emit: (event: NotificationEventName, payload: any) => this.dispatch(event, payload) };
    }
  }

  connect(): Promise<void> {
    if (!this.oidcAuthService.isAuthenticated()) return Promise.resolve();
    if (this.starting) return this.starting;
    if (this.connection && this.connection.state !== HubConnectionState.Disconnected) return Promise.resolve();

    if (!this.connection) this.connection = this.buildConnection();

    this.state.set('connecting');
    this.starting = this.connection.start()
      .then(() => this.state.set('connected'))
      .catch((err) => {
        console.warn('[notifications] unable to connect', err);
        this.state.set('disconnected');
      })
      .finally(() => (this.starting = null));
    return this.starting;
  }

  /** Reconnect after the automatic retries have been exhausted. */
  retry(): Promise<void> {
    return this.connect();
  }

  async disconnect(): Promise<void> {
    const connection = this.connection;
    this.connection = null;
    await connection?.stop();
    this.state.set('disconnected');
  }

  on<K extends NotificationEventName>(event: K): Observable<NotificationEventMap[K]> {
    return this.events$.pipe(
      filter((e): e is NotificationEnvelope<K> => e.event === event),
      map((e) => e.payload)
    );
  }

  private buildConnection(): HubConnection {
    const baseUrl = (this.envService.env.apiUrl ?? '').replace(/\/+$/, '');
    const tenantId = this.oidcAuthService.getTenantId() || this.envService.env.tenantId;

    const connection = new HubConnectionBuilder()
      .withUrl(`${baseUrl}/hubs/notifications`, {
        accessTokenFactory: () => this.oidcAuthService.accessToken,
        transport: HttpTransportType.WebSockets,
        headers: tenantId ? { TenantId: tenantId } : undefined,
        withCredentials: false // API CORS allows any origin but not credentials
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build();

    for (const event of EVENT_NAMES) {
      connection.on(event, (payload: any) => this.dispatch(event, payload));
    }

    connection.onreconnecting(() => this.zone.run(() => this.state.set('reconnecting')));
    connection.onreconnected(() => this.zone.run(() => this.state.set('connected')));
    connection.onclose((err) => {
      if (err) console.warn('[notifications] connection closed', err);
      this.zone.run(() => this.state.set('disconnected'));
    });

    return connection;
  }

  private dispatch(event: NotificationEventName, payload: any) {
    this.zone.run(() => this.events.next({ event, payload: payload ?? {}, receivedAt: new Date() }));
  }
}
